import { FC, ReactNode, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import { CallIntelTooltip } from '@frontend/call-intel-core';
import { AnimatedBadge } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading, Text, Truncated } from '@frontend/design-system';
import { useActiveFilters } from '../../../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../../../hooks/use-call-intel-demo-flags';

interface StatsHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  callIntelTooltipId: CallIntelTypes.InfoTipId;
  styles?: Interpolation<Theme>;
  showBetaBadge?: boolean;
  showNewBadge?: boolean;
  tooltipAutoOpen?: boolean;
  onTooltipAutoOpen?: () => void;
}
export const StatsHeader: FC<StatsHeaderProps> = ({
  callIntelTooltipId,
  title,
  subtitle,
  styles,
  showBetaBadge = false,
  showNewBadge = false,
  tooltipAutoOpen,
  onTooltipAutoOpen,
}) => {
  const { filterHintText } = useActiveFilters();
  const { t } = useTranslation('call-intelligence');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { isSmallMode } = useIsSmallMode();

  const renderHeading = useMemo(() => {
    return typeof title === 'string' ? (
      <Heading level={3} css={[defaultStyles.heading, isSmallMode && { fontSize: theme.font.size.large }]}>
        <Truncated>{title}</Truncated>
      </Heading>
    ) : (
      title
    );
  }, [title, isSmallMode]);
  return (
    <div css={[defaultStyles.header, styles]}>
      <div css={defaultStyles.titleRow}>
        {showDemoChipAndBanner && <DemoChip />}
        {((showBetaBadge && !showDemoChipAndBanner) || showNewBadge) && (
          <AnimatedBadge
            label={showNewBadge ? t('New') : t('Beta')}
            animated={showNewBadge ? true : false}
            bgColor={showNewBadge ? undefined : theme.colors.indigo50}
          />
        )}
        {renderHeading}
        <CallIntelTooltip tip={callIntelTooltipId} autoOpen={tooltipAutoOpen} onAutoOpen={onTooltipAutoOpen} />
      </div>
      <Text size='medium' color='light' weight='bold' css={defaultStyles.filterText}>
        {filterHintText}
      </Text>
      <div css={defaultStyles.subtitle}>
        {typeof subtitle === 'string' ? <Text size='medium'>{subtitle}</Text> : subtitle}
      </div>
    </div>
  );
};

const defaultStyles = {
  header: css`
    display: grid;
    row-gap: ${theme.spacing(0.5)};
    column-gap: ${theme.spacing(2)};
    grid-template-columns: max(100px, 1fr) auto;
    grid-template-rows: auto;
    grid-template-areas: 'title filterText' 'subtitle subtitle';
    align-items: center;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      grid-template-areas: 'title filterText' 'subtitle filterText';
      align-items: start;
    }
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    min-width: 100px;
    grid-area: title;
  `,
  heading: css`
    display: flex;
    align-items: center;
    flex-shrink: 1;
    min-width: 0;
  `,
  filterText: css`
    width: fit-content;
    flex-shrink: 0;
    grid-area: filterText;
    justify-self: end;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      grid-row: 1 / span 2;
      align-self: start;
    }
  `,
  subtitle: css`
    grid-area: subtitle;
  `,
};
